import React from 'react';
import { MOODS, MoodId, getMood } from './moods';
import { MoodCheckInRecord } from '../../contexts/MoodContext';

/**
 * MoodCharts — small, accessible distribution visuals computed from real records.
 *
 * Deliberately lightweight (CSS conic-gradient ring + CSS bars) so they stay calm,
 * theme-aware and fully labelled with text — every chart has a readable summary and
 * a legend, never colour alone. They describe what was recorded and nothing more:
 * no trend claims, no causation, no diagnosis.
 */

export interface MoodCount {
  mood: MoodId;
  count: number;
  pct: number;
}

export function distribution(records: MoodCheckInRecord[]): { rows: MoodCount[]; total: number; top?: MoodCount } {
  const total = records.length;
  const counts = new Map<MoodId, number>();
  records.forEach((r) => counts.set(r.mood, (counts.get(r.mood) || 0) + 1));
  const rows = MOODS.map((m) => {
    const count = counts.get(m.id) || 0;
    return { mood: m.id, count, pct: total ? Math.round((count / total) * 100) : 0 };
  })
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
  return { rows, total, top: rows[0] };
}

export const summarySentence = (records: MoodCheckInRecord[], period: string): string => {
  const { total, top } = distribution(records);
  if (total === 0) return `No mood check-ins recorded ${period}.`;
  const topMood = getMood(top?.mood);
  return `${total} mood check-in${total === 1 ? '' : 's'} ${period}${topMood ? `. Your most frequently selected mood was ${topMood.label}.` : '.'}`;
};

/** A donut ring of the mood mix (conic-gradient), with a legend and a text label. */
export const MoodDonut: React.FC<{ records: MoodCheckInRecord[] }> = ({ records }) => {
  const { rows, total } = distribution(records);

  if (total === 0) {
    return <p className="mm-empty-sub">No check-ins to chart yet.</p>;
  }

  // Build conic stops from cumulative percentages (uses exact fractions so they sum to 360°).
  let acc = 0;
  const stops = rows
    .map((r) => {
      const start = (acc / total) * 100;
      acc += r.count;
      const end = (acc / total) * 100;
      return `${getMood(r.mood)?.accent} ${start}% ${end}%`;
    })
    .join(', ');

  return (
    <div className="mm-donut-wrap">
      <div
        className="mm-donut"
        style={{ background: `conic-gradient(${stops})` }}
        role="img"
        aria-label={rows.map((r) => `${getMood(r.mood)?.label} ${r.pct}%`).join(', ')}
      >
        <div className="mm-donut__hole">
          <span className="mm-donut__total">{total}</span>
          <small className="mm-donut__cap">check-ins</small>
        </div>
      </div>
      <ul className="mm-legend">
        {rows.map((r) => {
          const m = getMood(r.mood);
          return (
            <li key={r.mood} className="mm-legend__item">
              <span className="mm-legend__dot" style={{ background: m?.accent }} aria-hidden="true" />
              <span className="mm-legend__label">{m?.emoji} {m?.label}</span>
              <span className="mm-legend__val">{r.pct}% · {r.count}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** Horizontal bars — same data as the donut, better for exact comparison. */
export const MoodBars: React.FC<{ records: MoodCheckInRecord[] }> = ({ records }) => {
  const { rows, total } = distribution(records);
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (total === 0) return <p className="mm-empty-sub">No check-ins to chart yet.</p>;
  return (
    <ul className="mm-insights__bars">
      {rows.map((r) => {
        const m = getMood(r.mood);
        return (
          <li key={r.mood} className="mm-insights__row">
            <span className="mm-insights__name"><span aria-hidden="true">{m?.emoji}</span> {m?.label}</span>
            <span className="mm-insights__track">
              <span className="mm-insights__fill" style={{ width: `${Math.round((r.count / max) * 100)}%`, background: m?.accent }} />
            </span>
            <span className="mm-insights__count">{r.count}</span>
          </li>
        );
      })}
    </ul>
  );
};
